import {
  PropertyConfigurationIN,
  PropertyConfigurationOUT,
  PropertyTypeChoices,
  productPropertyConfigurationApi,
} from "api-client";
import { Property } from "features/BuildingProductEditor/ProductEditorStore";
import { sidebarMenuStore } from "features/SidebarMenu/SidebarMenuStore";
import { makeAutoObservable } from "mobx";

class PropertyManager {
  allPropertyConfigs: Property[] = [];
  lastIndexFocused: number | null = null;

  setAllProperties(properties: PropertyConfigurationOUT[]) {
    this.allPropertyConfigs = properties.map((item) => ({
      ...item,
      options: item.options.choices ?? [],
      value: undefined,
    }));
  }

  setLastIndexFocused(lastIndexFocused: number | null) {
    this.lastIndexFocused = lastIndexFocused;
  }

  async getAllPropertyConfigs(propertyType?: PropertyTypeChoices | null) {
    const orgID = sidebarMenuStore.currentOrganisation?.id;
    if (!orgID) return;
    return await productPropertyConfigurationApi()
      .buildingProductsApiV1RoutersPropertyConfigurationGetAllPropertyConfigs(
        orgID,
        propertyType
      )
      .then((response) => this.setAllProperties(response.data))
      .catch((err) => console.error("getAllPropertyConfig", err));
  }

  async getSinglePropertyConfig(propertySlug: string) {
    const orgID = sidebarMenuStore.currentOrganisation?.id;
    if (!orgID) return;
    return await productPropertyConfigurationApi()
      .buildingProductsApiV1RoutersPropertyConfigurationGetSinglePropertyConfig(
        propertySlug,
        orgID
      )
      .catch((err) => console.error("GetSinglePropertyConfig", err));
  }

  private getProps(property: Property) {
    let choices: string[] = [];
    if (
      property.property_type === PropertyTypeChoices.Select ||
      property.property_type === PropertyTypeChoices.Multiselect
    ) {
      choices = property?.options as string[];
    }

    const props: PropertyConfigurationIN = {
      name: property.name,
      organisation_id: sidebarMenuStore.currentOrganisation?.id,
      property_type: property.property_type,
      options: { choices: choices },
    };
    return props;
  }

  async createProperty(property: Property) {
    if (property.name === "") return;
    const props: PropertyConfigurationIN = this.getProps(property);
    return await productPropertyConfigurationApi()
      .buildingProductsApiV1RoutersPropertyConfigurationCreatePropertyConfiguration(
        props
      )
      .then(async (response) => {
        await this.getAllPropertyConfigs();
        return response.data;
      })
      .catch((err) => console.error("createProperty", err));
  }

  async removeProperty(propertySlug: string) {
    const orgID = sidebarMenuStore.currentOrganisation?.id;
    if (!orgID) return;
    await productPropertyConfigurationApi()
      .buildingProductsApiV1RoutersPropertyConfigurationRemovePropertyConfiguration(
        orgID,
        propertySlug
      )
      .catch((err) => console.error("removeProperty", err));
    await this.getAllPropertyConfigs();
  }

  async updateProperty(property: Property) {
    const props: PropertyConfigurationIN = this.getProps(property);
    return await productPropertyConfigurationApi()
      .buildingProductsApiV1RoutersPropertyConfigurationUpdatePropertyConfiguration(
        property.slug,
        props
      )
      .then((response) => {
        this.getAllPropertyConfigs();
        return response.data;
      })
      .catch((err) => console.error("updateProperty", err));
  }

  async updateOrdering(properties: Property[]) {
    const orgID = sidebarMenuStore.currentOrganisation?.id;
    if (!orgID) return;
    await productPropertyConfigurationApi().buildingProductsApiV1RoutersPropertyConfigurationUpdateOrdering(
      orgID,
      {
        ordered_slugs: properties.map((item, index) => ({
          slug: item.slug,
          position: index,
        })),
      }
    );
  }

  async checkPropertyValueIsInUse(propertySlug: string, option?: string) {
    const orgID = sidebarMenuStore.currentOrganisation?.id;
    if (!orgID) return;
    return await productPropertyConfigurationApi()
      .buildingProductsApiV1RoutersPropertyConfigurationGetPropertyValueInUse(
        propertySlug,
        orgID,
        option as string
      )
      .then((response) => response.data);
  }

  constructor() {
    makeAutoObservable(this);
  }

  static instance: PropertyManager;
  static getInstance(): PropertyManager {
    if (!PropertyManager.instance) {
      PropertyManager.instance = new PropertyManager();
    }
    return PropertyManager.instance;
  }
}
export const propertyManagerStore = PropertyManager.getInstance();
